// ChatMessage Class - Represents a single message in a chat conversation
// Supports multimodal content (text, images, audio, video, files)
// Compatible with OpenRouter API format
// Uses segment-based architecture for fine-grained capability control
// Supports JSON serialization/deserialization for persistence

export class ChatMessage {
  constructor() {
    // Message metadata
    this.data = {
      role: null, // 'user' | 'assistant' | 'system' | 'tool' | 'developer'
      name: null, // string - optional name for the message
      segments: [] // Array of segment objects in order - this is the primary content structure
    };
    
    // Aggregated capabilities from all segments
    this.capabilities = new Set();
  }

  /**
   * Update aggregated capabilities from all segments
   */
  _updateAggregatedCapabilities() {
    const allCapabilities = new Set();
    for (const segment of this.data.segments) {
      for (const capability of segment.capabilities || []) {
        allCapabilities.add(capability);
      }
    }
    this.capabilities = allCapabilities;
  }

  /**
   * Detect capabilities for a segment based on its content
   * @param {Object} segment - Segment object to detect capabilities for
   * @returns {string[]} Array of detected capability strings
   */
  detectSegmentCapabilities(segment) {
    if (!segment) return [];
    
    const detected = [];
    
    // Text capability is implicit for text segments
    if (segment.type === 'text' && segment.content) {
      detected.push('text');
    }

    // Check content for multimodal capabilities
    if (segment.content) {
      const content = segment.content;
      
      // Handle array content (OpenRouter format)
      if (Array.isArray(content)) {
        for (const item of content) {
          if (typeof item === 'object' && item !== null) {
            // Check for image
            if (item.type === 'image' || item.image_url || item.image) {
              detected.push('Image Input');
            }
            // Check for audio
            if (item.type === 'audio' || item.audio_url || item.audio) {
              detected.push('Audio Input');
            }
            // Check for video
            if (item.type === 'video' || item.video_url || item.video) {
              detected.push('Video Input');
            }
            // Check for file
            if (item.type === 'file' || item.file_url || item.file) {
              detected.push('File Input');
            }
          }
        }
      } else if (typeof content === 'string') {
        // Check for base64 image data in string
        if (content.includes('data:image/') || content.match(/data:image\/[^;]+;base64,/)) {
          detected.push('Image Input');
        }
        // Check for image URLs
        if (content.match(/\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i)) {
          detected.push('Image Input');
        }
      }
    }

    // Type-specific capabilities
    if (segment.type === 'image') {
      detected.push('Image Input');
    } else if (segment.type === 'audio') {
      detected.push('Audio Input');
    } else if (segment.type === 'video') {
      detected.push('Video Input');
    } else if (segment.type === 'file') {
      detected.push('File Input');
    } else if (segment.type === 'function_call') {
      detected.push('Function Calling');
    } else if (segment.type === 'reasoning') {
      detected.push('Thinking');
    } else if (segment.type === 'structured') {
      detected.push('Structured Outputs');
    } else if (segment.type === 'web_search') {
      detected.push('Web Search');
    }

    // Update segment capabilities
    segment.capabilities = [...new Set([...segment.capabilities || [], ...detected])];
    return segment.capabilities;
  }

  /**
   * Add a new segment to the message
   * @param {Object} segmentData - Segment data object
   * @param {string} segmentData.type - Segment type
   * @param {*} segmentData.content - Segment content
   * @param {string[]} [segmentData.capabilities] - Optional capability tags
   * @param {boolean} [segmentData.blocking] - Whether segment is blocking (default: false)
   * @param {Date} [segmentData.startTimestamp] - Start timestamp (default: now)
   * @param {Date} [segmentData.endTimestamp] - End timestamp (default: now)
   * @param {Object} [segmentData.formats] - Format representations
   * @param {string} [segmentData.activeFormat] - Currently active format
   * @param {number|null} [position] - Optional position to insert at (default: end)
   * @returns {number} Index of the added segment
   */
  addSegment(segmentData, position = null) {
    const now = new Date();
    const segment = {
      type: segmentData.type,
      content: segmentData.content,
      capabilities: segmentData.capabilities || [],
      blocking: segmentData.blocking !== undefined ? segmentData.blocking : false,
      startTimestamp: segmentData.startTimestamp || now,
      endTimestamp: segmentData.endTimestamp || now,
      formats: segmentData.formats || {},
      activeFormat: segmentData.activeFormat || null
    };

    // Detect capabilities if not provided
    if (segment.capabilities.length === 0) {
      this.detectSegmentCapabilities(segment);
    }

    // Insert at position or append
    if (position === null || position >= this.data.segments.length) {
      this.data.segments.push(segment);
    } else if (position < 0) {
      this.data.segments.unshift(segment);
    } else {
      this.data.segments.splice(position, 0, segment);
    }

    this._updateAggregatedCapabilities();
    return this.data.segments.length - 1;
  }

  /**
   * Get all segments
   * @returns {Array<Object>} Array of segment objects
   */
  getSegments() {
    return [...this.data.segments];
  }

  /**
   * Get a specific segment by index
   * @param {number} index - Segment index
   * @returns {Object|null} Segment object or null if not found
   */
  getSegment(index) {
    if (index < 0 || index >= this.data.segments.length) {
      return null;
    }
    return { ...this.data.segments[index] };
  }

  /**
   * Remove a segment by index
   * @param {number} index - Segment index to remove
   * @returns {boolean} True if segment was removed, false if not found
   */
  removeSegment(index) {
    if (index < 0 || index >= this.data.segments.length) {
      return false;
    }
    this.data.segments.splice(index, 1);
    this._updateAggregatedCapabilities();
    return true;
  }

  /**
   * Update a segment by index
   * @param {number} index - Segment index to update
   * @param {Object} segmentData - New segment data (will merge with existing)
   * @returns {boolean} True if segment was updated, false if not found
   */
  updateSegment(index, segmentData) {
    if (index < 0 || index >= this.data.segments.length) {
      return false;
    }

    const segment = this.data.segments[index];
    Object.assign(segment, segmentData);

    // Re-detect capabilities if content changed
    if (segmentData.content !== undefined) {
      this.detectSegmentCapabilities(segment);
    }

    this._updateAggregatedCapabilities();
    return true;
  }

  /**
   * Get all capabilities from all segments
   * @returns {string[]} Array of capability strings
   */
  getCapabilities() {
    return Array.from(this.capabilities);
  }

  /**
   * Get capabilities from blocking segments only
   * @returns {string[]} Array of capability strings
   */
  getBlockingCapabilities() {
    const blocking = new Set();
    for (const segment of this.data.segments) {
      if (segment.blocking === true) {
        for (const capability of segment.capabilities || []) {
          blocking.add(capability);
        }
      }
    }
    return Array.from(blocking);
  }

  /**
   * Get capabilities from non-blocking segments only
   * @returns {string[]} Array of capability strings
   */
  getNonBlockingCapabilities() {
    const nonBlocking = new Set();
    for (const segment of this.data.segments) {
      if (segment.blocking === false) {
        for (const capability of segment.capabilities || []) {
          nonBlocking.add(capability);
        }
      }
    }
    return Array.from(nonBlocking);
  }

  /**
   * Check if message has a specific capability
   * @param {string} capability - Capability name to check
   * @returns {boolean} True if message has the capability
   */
  hasCapability(capability) {
    return this.capabilities.has(capability);
  }

  /**
   * Check if message has a blocking capability
   * @param {string} capability - Capability name to check
   * @returns {boolean} True if message has the capability and it's blocking
   */
  hasBlockingCapability(capability) {
    for (const segment of this.data.segments) {
      if (segment.blocking === true && (segment.capabilities || []).includes(capability)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Convert segments to OpenRouter API format
   * Always returns a consistent structure: content is either a string (single text) or an array of content item objects
   * @returns {Object} OpenRouter-compatible message object
   */
  toOpenRouterFormat() {
    const result = {
      role: this.data.role,
      content: null,
      name: this.data.name,
      tool_calls: this.data.tool_calls || null, // Check message data first
      tool_call_id: null,
      reasoning: null,
    };

    // Special handling for tool messages - they have simple format
    if (this.data.role === "tool") {
      // For tool messages, find the tool_call_id and content
      for (const segment of this.data.segments) {
        if (segment.type === "function_call" && segment.content?.tool_call_id) {
          result.tool_call_id = segment.content.tool_call_id;
        } else if (segment.type === "text" && segment.content) {
          result.content = segment.content; // Raw tool result string
        }
      }
      return result;
    }

    // Build content array from all segments
    const contentItems = [];
    const textSegments = [];

    for (const segment of this.data.segments) {
      if (segment.type === "text" && segment.content) {
        // Collect text segments separately to handle single text case
        textSegments.push(segment.content);
        // Also add to content items for consistency
        contentItems.push({ type: "text", text: segment.content });
      } else if (segment.type === "image") {
        contentItems.push({
          type: "image_url",
          image_url: { url: segment.content },
        });
      } else if (segment.type === "audio") {
        contentItems.push({
          type: "audio_url",
          audio_url: { url: segment.content },
        });
      } else if (segment.type === "video") {
        contentItems.push({
          type: "video_url",
          video_url: { url: segment.content },
        });
      } else if (segment.type === "file") {
        contentItems.push({
          type: "file_url",
          file_url: { url: segment.content },
        });
      } else if (segment.type === "function_call") {
        // Handle function_call segments - tool_calls can be in different formats
        if (segment.content?.tool_calls && Array.isArray(segment.content.tool_calls)) {
          // If we have multiple function_call segments, we need to merge them
          if (!result.tool_calls) {
            result.tool_calls = [];
          }
          result.tool_calls.push(...segment.content.tool_calls);
        } else if (Array.isArray(segment.content)) {
          // Legacy format: content is directly an array of tool calls
          if (!result.tool_calls) {
            result.tool_calls = [];
          }
          result.tool_calls.push(...segment.content);
        } else if (segment.content?.tool_call_id) {
          result.tool_call_id = segment.content.tool_call_id;
        }
      } else if (segment.type === "reasoning" || segment.type === "thinking") {
        // Both "reasoning" and "thinking" map to the reasoning field in OpenRouter format
        // If multiple reasoning segments exist, concatenate them
        if (result.reasoning) {
          result.reasoning += segment.content;
        } else {
        result.reasoning = segment.content;
        }
      }
    }

    // Set content: use string for text-only content, array for multimodal content
    // Reasoning and tool_calls are handled as separate top-level fields, not content
    if (contentItems.length === 0) {
      // When assistant message has tool_calls and no text content, content should be null
      // (OpenRouter/OpenAI spec: content can be null when tool_calls are present)
      // For user messages or assistant messages without tool_calls, use empty string
      if (result.role === "assistant" && result.tool_calls && result.tool_calls.length > 0) {
        result.content = null;
      } else {
        result.content = "";
      }
    } else if (contentItems.length === 1 && contentItems[0].type === "text") {
      // Single text segment - return as string (OpenRouter standard for simple text)
      result.content = textSegments[0];
    } else {
      // Multiple text segments or multimodal content (images/audio/video/files) - return as array
      result.content = contentItems;
    }

    return result;
  }

  /**
   * Legacy method: Get capabilities as an array
   * @returns {string[]} Array of capability strings
   */
  getCapabilitiesArray() {
    return this.getCapabilities();
  }

  /**
   * Legacy method: Manually add a capability tag
   * @param {string} capability - Capability name to add
   */
  addCapability(capability) {
    this.capabilities.add(capability);
    // Also add to first segment if it exists
    if (this.data.segments.length > 0) {
      const segment = this.data.segments[0];
      if (!segment.capabilities) {
        segment.capabilities = [];
      }
      if (!segment.capabilities.includes(capability)) {
        segment.capabilities.push(capability);
      }
    }
  }

  /**
   * Legacy method: Manually remove a capability tag
   * @param {string} capability - Capability name to remove
   */
  removeCapability(capability) {
    this.capabilities.delete(capability);
    // Remove from all segments
    for (const segment of this.data.segments) {
      if (segment.capabilities) {
        const index = segment.capabilities.indexOf(capability);
        if (index > -1) {
          segment.capabilities.splice(index, 1);
        }
      }
    }
  }

  /**
   * Serialize the ChatMessage to a JSON-compatible object
   * @returns {Object} JSON-serializable representation
   */
  toJSON() {
    // Create serializable version with ISO string timestamps
    const serializable = {
      data: {
        ...this.data, // Include ALL message data
        segments: this.data.segments.map(segment => ({
          ...segment,
          startTimestamp: segment.startTimestamp instanceof Date
            ? segment.startTimestamp.toISOString()
            : segment.startTimestamp,
          endTimestamp: segment.endTimestamp instanceof Date
            ? segment.endTimestamp.toISOString()
            : segment.endTimestamp
        }))
      }
    };
    return serializable;
  }

  /**
   * Deserialize JSON data and update this ChatMessage instance
   * Clears current data and replaces with new data
   * @param {Object} jsonData - JSON data from toJSON()
   */
  fromJSON(jsonData) {
    // Clear current data
    this.data = {
      role: null,
      name: null,
      segments: []
    };
    this.capabilities.clear();

    // Restore all data
    this.data = {
      ...jsonData.data, // Restore ALL data
      segments: jsonData.data.segments.map(segment => ({
        ...segment,
        startTimestamp: segment.startTimestamp
          ? new Date(segment.startTimestamp)
          : new Date(),
        endTimestamp: segment.endTimestamp
          ? new Date(segment.endTimestamp)
          : new Date()
      }))
    };

    // Rebuild aggregated capabilities
    this._updateAggregatedCapabilities();
  }
}
